import {
  AccountCircleOutlined,
  Add,
  ArrowRightAlt,
  Delete,
  Edit,
  Folder,
  FolderOutlined,
  MoreHoriz,
  OpenInNew,
  Download,
  Verified,
  Update,
  Lock,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  Tooltip,
  Typography,
  useTheme,
  alpha,
} from "@mui/material";
import { font } from "../../styles/font";
import { blue, brown, green, grey, red, orange, purple } from "@mui/material/colors";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { LightTooltip } from "../styled/CustomComponent";
import React from "react";

// ────────────────────────────────────────────────────────────
// Status enums — covers both story statuses and tool statuses
// ────────────────────────────────────────────────────────────
class STORY_STATUS {
  static DRAFT = "Draft";
  static APPROVED = "Approved";
  static UNDER_DEVELOPMENT = "Under development";
  static CLOSED = "Closed";
}

class TOOL_STATUS {
  static INSTALLED = "Installed";
  static NOT_INSTALLED = "Not Installed";
  static UPDATE_AVAILABLE = "Update available";
}

// ────────────────────────────────────────────────────────────
// Color resolver — works for story + tool statuses
// ────────────────────────────────────────────────────────────
function getStatusColor(status, theme) {
  const s = status?.toLowerCase?.() ?? "";

  // Story statuses
  if (s === STORY_STATUS.DRAFT.toLowerCase()) return brown[500];
  if (s === STORY_STATUS.APPROVED.toLowerCase()) return green[500];
  if (s === STORY_STATUS.UNDER_DEVELOPMENT.toLowerCase()) return blue[500];
  if (s === STORY_STATUS.CLOSED.toLowerCase()) return red[500];

  // Tool statuses
  if (s === TOOL_STATUS.INSTALLED.toLowerCase()) return green[500];
  if (s === TOOL_STATUS.NOT_INSTALLED.toLowerCase()) return grey[500];
  if (s === TOOL_STATUS.UPDATE_AVAILABLE.toLowerCase()) return orange[600];

  return theme?.palette?.mode === "dark" ? grey[400] : grey[500];
}

// ────────────────────────────────────────────────────────────
// Small reusable status pill
// ────────────────────────────────────────────────────────────
function StatusPill({ status, theme }) {
  const color = getStatusColor(status, theme);
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 0.9,
        py: 0.2,
        borderRadius: 999,
        backgroundColor: alpha(color, 0.12),
        color,
        fontSize: font.size.fs_4 ?? "0.75rem",
        fontWeight: 600,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
        border: `1px solid ${alpha(color, 0.3)}`,
      }}
    >
      <Box
        component="span"
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: color,
          flexShrink: 0,
        }}
      />
      {status}
    </Box>
  );
}

// ────────────────────────────────────────────────────────────
// Icon glyph rendered from `item.icon` + `item.color`
// Falls back to a themed letter tile if no icon is provided
// ────────────────────────────────────────────────────────────
function ToolIcon({ item, size = 36 }) {
  const letter = item?.icon ?? item?.name?.charAt(0)?.toUpperCase() ?? "?";
  const bg = item?.color ?? "bg-blue-500";

  // Map tailwind-ish colour tokens to hex so it works inside MUI
  const bgMap = {
    "bg-red-500": red[500],
    "bg-blue-500": blue[500],
    "bg-green-500": green[500],
    "bg-purple-500": purple[500],
    "bg-emerald-500": green[600],
    "bg-indigo-500": "#6366f1",
    "bg-pink-500": "#ec4899",
    "bg-teal-500": "#14b8a6",
    "bg-orange-500": orange[600],
    "bg-cyan-500": "#06b6d4",
  };

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: 2,
        backgroundColor: bgMap[bg] ?? blue[500],
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: size * 0.42,
        flexShrink: 0,
        boxShadow: `0 2px 6px ${alpha(bgMap[bg] ?? blue[500], 0.35)}`,
        userSelect: "none",
      }}
      aria-hidden="true"
    >
      {letter}
    </Box>
  );
}

// ────────────────────────────────────────────────────────────
// Main card
// ────────────────────────────────────────────────────────────
function MarketplaceCard({
  item,
  handleClick,
  handleInstall,
  handleOpenTool,
  handleUpdate,
  showMenu = true,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Determine whether this card represents a story or a marketplace tool
  const isTool = Boolean(item?.type);
  const status = item?.status;

  // Contextual action menu — differs for stories vs tools
  const menuOptions = isTool
    ? [
        {
          label: "Open details",
          icon: <OpenInNew sx={{ color: theme.palette.primary.main, fontSize: 16 }} />,
          onClick: () => handleClick?.(item),
        },
        {
          label:
            status === TOOL_STATUS.INSTALLED
              ? "Reinstall"
              : status === TOOL_STATUS.UPDATE_AVAILABLE
              ? "Update"
              : "Install",
          icon:
            status === TOOL_STATUS.UPDATE_AVAILABLE ? (
              <Update sx={{ color: orange[600], fontSize: 16 }} />
            ) : (
              <Download sx={{ color: green[600], fontSize: 16 }} />
            ),
          onClick: () =>
            status === TOOL_STATUS.UPDATE_AVAILABLE
              ? handleUpdate?.(item)
              : handleInstall?.(item),
        },
        {
          label: "Remove from workspace",
          icon: <Delete color="error" sx={{ fontSize: 16 }} />,
          onClick: () => console.log("Remove tool"),
        },
      ]
    : [
        {
          label: "Duplicate Story",
          icon: <FolderOutlined sx={{ color: theme.palette.primary.main, fontSize: 16 }} />,
          onClick: () => console.log("Duplicate Story"),
        },
        {
          label: "Edit Story",
          icon: <Edit sx={{ color: theme.palette.secondary.main, fontSize: 16 }} />,
          onClick: () => console.log("Edit Story"),
        },
        {
          label: "Delete Story",
          icon: <Delete color="error" sx={{ fontSize: 16 }} />,
          onClick: () => console.log("Delete Story"),
        },
      ];

  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const menuOpen = Boolean(menuAnchor);

  const handleMenuOpen = (e) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };
  const handleMenuClose = (e) => {
    e?.stopPropagation?.();
    setMenuAnchor(null);
  };

  // Primary action icon shown in the header
  const primaryActionIcon = (() => {
    if (!isTool) return <Add />;
    if (status === TOOL_STATUS.INSTALLED) return <OpenInNew />;
    if (status === TOOL_STATUS.UPDATE_AVAILABLE) return <Update />;
    return <Download />;
  })();

  const primaryActionTooltip = (() => {
    if (!isTool) return "Use story";
    if (status === TOOL_STATUS.INSTALLED)
      return item?.secondaryAction ?? "Open tool";
    if (status === TOOL_STATUS.UPDATE_AVAILABLE) return "Update tool";
    return item?.primaryAction ?? "Install";
  })();

  const handlePrimaryAction = (e) => {
    e.stopPropagation();
    if (!isTool) {
      console.log("use story");
      return;
    }
    if (status === TOOL_STATUS.INSTALLED) {
      handleOpenTool?.(item);
    } else if (status === TOOL_STATUS.UPDATE_AVAILABLE) {
      handleUpdate?.(item);
    } else {
      handleInstall?.(item);
    }
  };

  return (
    <Box
      className="flex flex-col w-full h-full shadow-sm rounded-lg"
      sx={{
        backgroundColor: isDark ? theme.palette.background.paper : "#fff",
        color: theme.palette.text.primary,
        border: `1px solid ${alpha(theme.palette.divider, isDark ? 0.35 : 0.6)}`,
        transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: isDark
            ? `0 8px 24px ${alpha("#000", 0.45)}`
            : `0 8px 24px ${alpha("#000", 0.08)}`,
          borderColor: alpha(theme.palette.primary.main, 0.45),
        },
      }}
    >
      {/* ───── Header ───── */}
      <Box
        className="flex items-center gap-2 px-3 py-2"
        sx={{ borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}` }}
      >
        {isTool && <ToolIcon item={item} size={32} />}

        <Box className="flex flex-col min-w-0 flex-1">
          <Typography
            fontSize={font.size.fs_4}
            fontWeight={600}
            className="truncate"
            title={item?.name}
          >
            {item?.name}
          </Typography>

          {isTool && item?.type && (
            <Typography
              fontSize={font.size.fs_4}
              sx={{ color: theme.palette.text.secondary, lineHeight: 1.2 }}
              className="truncate"
            >
              {item.type}
            </Typography>
          )}
        </Box>

        {isTool && status && <StatusPill status={status} theme={theme} />}

        <LightTooltip title={primaryActionTooltip} placement="top" arrow>
          <IconButton
            size="small"
            color="primary"
            aria-label={primaryActionTooltip}
            onClick={handlePrimaryAction}
            sx={{
              fontSize: font.size.fs_6,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
              borderRadius: 1.5,
              p: 0.5,
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            {primaryActionIcon}
          </IconButton>
        </LightTooltip>

        {showMenu && (
          <>
            <IconButton
              size="small"
              aria-label="More options"
              onClick={handleMenuOpen}
              sx={{
                color: theme.palette.text.secondary,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.text.primary, 0.06),
                },
              }}
            >
              <MoreHoriz fontSize="small" />
            </IconButton>

            <Menu
              anchorEl={menuAnchor}
              open={menuOpen}
              onClose={handleMenuClose}
              onClick={(e) => e.stopPropagation()}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  minWidth: 200,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                  boxShadow: isDark
                    ? `0 8px 24px ${alpha("#000", 0.5)}`
                    : `0 8px 24px ${alpha("#000", 0.08)}`,
                },
              }}
            >
              {menuOptions.map((opt) => (
                <MenuItem
                  key={opt.label}
                  onClick={(e) => {
                    handleMenuClose(e);
                    opt.onClick();
                  }}
                  sx={{ gap: 1.25, fontSize: font.size.fs_4 }}
                >
                  {opt.icon}
                  <Typography fontSize={font.size.fs_4}>{opt.label}</Typography>
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </Box>

      {/* ───── Body (clickable) ───── */}
      <Box
        className="flex items-start gap-2 px-3 pt-2.5 flex-1 cursor-pointer"
        onClick={() => handleClick?.(item)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick?.(item);
          }
        }}
      >
        <Box className="flex flex-col gap-0.5 min-w-0 flex-1">
          {isTool && item?.title && (
            <Typography
              fontSize={font.size.fs_4}
              fontWeight={600}
              sx={{ color: theme.palette.text.primary, lineHeight: 1.25 }}
              className="line-clamp-2"
            >
              {item.title}
            </Typography>
          )}

          {item?.description && (
            <Typography
              fontSize={font.size.fs_4}
              sx={{
                color: theme.palette.text.secondary,
                lineHeight: 1.4,
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {item.description}
            </Typography>
          )}
        </Box>
      </Box>

      {/* ───── Footer stats ───── */}
      <Box
        className="flex items-end justify-between px-3 py-2.5 mt-1"
        sx={{ borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}
      >
        {isTool ? (
          <>
            {/* Version + compatibility */}
            <Box className="flex items-center gap-1.5 min-w-0">
              {item?.version && (
                <Chip
                  size="small"
                  label={`v${item.version}`}
                  sx={{
                    height: 20,
                    fontSize: font.size.fs_4,
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    "& .MuiChip-label": { px: 1 },
                  }}
                />
              )}
              {item?.author && (
                <Typography
                  fontSize={font.size.fs_4}
                  sx={{ color: theme.palette.text.secondary }}
                  className="truncate"
                >
                  by {item.author}
                </Typography>
              )}
            </Box>

            {/* Last sync / verified */}
            <LightTooltip
              arrow
              placement="top"
              title={
                item?.lastSync
                  ? `Last synced ${item.lastSync}`
                  : "Not yet synced"
              }
            >
              <Box className="flex items-center gap-1 flex-shrink-0">
                {status === TOOL_STATUS.INSTALLED ? (
                  <Verified sx={{ color: green[500], fontSize: font.size.fs_4 }} />
                ) : (
                  <Lock
                    sx={{
                      color: theme.palette.text.disabled,
                      fontSize: font.size.fs_4,
                    }}
                  />
                )}
                <Typography
                  fontSize={font.size.fs_4}
                  sx={{ color: theme.palette.text.secondary }}
                >
                  {item?.lastSync ?? "Never"}
                </Typography>
              </Box>
            </LightTooltip>
          </>
        ) : (
          <>
            {/* Story usage stats (original behaviour) */}
            <LightTooltip arrow placement="top" title={`Used by 10K users`}>
              <Box className="flex items-center gap-1">
                <Typography fontSize={font.size.fs_4}>10K</Typography>
                <TrendingUpIcon
                  sx={{ color: green[500], fontSize: font.size.fs_4 }}
                />
              </Box>
            </LightTooltip>

            <LightTooltip
              arrow
              placement="top"
              title={`You have used this story 11 times`}
            >
              <Box className="flex items-center gap-1">
                <Typography fontSize={font.size.fs_4}>11</Typography>
                <Folder
                  sx={{
                    color: theme.palette.primary.main,
                    fontSize: font.size.fs_4,
                  }}
                />
              </Box>
            </LightTooltip>
          </>
        )}
      </Box>
    </Box>
  );
}

export default MarketplaceCard;